import { useCallback, useEffect } from "react";
import type { UseFormWatch } from "react-hook-form";
import type { BlockValues } from "@circle-react-shared/BuilderUI/BuilderUI.types";
import { serializableInvariant } from "@circle-react-shared/BuilderUI/Helpers/isSerializable";
import { useBuilderStore } from "../../Hooks/useBuilderStore";
import { updateBlockProp, updateBlockStyles } from "../utils/updateBlock";

interface Props {
  watch: UseFormWatch<BlockValues>;
}

export const useBlockFormWatch = ({ watch }: Props) => {
  // Combined subscription for all builder store values
  const {
    blockMapping,
    currentCanvasTheme,
    appearance,
    canvasSize,
    updateBlock,
    selectedBlock,
    shouldProcessResponsiveOverrides,
  } = useBuilderStore(state => {
    const value = state.getSelectedBlock();
    return {
      blockMapping: state.blockMapping,
      currentCanvasTheme: state.currentCanvasTheme,
      appearance: state.appearance,
      canvasSize: state.canvasSize,
      updateBlock: state.updateSelectedBlock,
      selectedBlock: value?.block,
      shouldProcessResponsiveOverrides: state.shouldProcessResponsiveOverrides,
    };
  });

  const memoizedUpdateBlock = useCallback(
    (formData: BlockValues, blockToUpdate: typeof selectedBlock) => {
      if (!formData || !blockToUpdate) return;

      let updatedBlock = structuredClone(blockToUpdate);

      Object.entries(formData).forEach(([key, formValue]) => {
        if (key === "styles") {
          updatedBlock = updateBlockStyles(
            updatedBlock,
            formValue,
            currentCanvasTheme,
            appearance,
            canvasSize,
            shouldProcessResponsiveOverrides,
          );
        } else {
          updatedBlock = updateBlockProp(
            updatedBlock,
            key,
            formValue,
            blockMapping,
          );
        }
      });

      serializableInvariant(updatedBlock);
      updateBlock(updatedBlock);
    },
    [
      updateBlock,
      blockMapping,
      currentCanvasTheme,
      appearance,
      canvasSize,
      shouldProcessResponsiveOverrides,
    ],
  );

  useEffect(() => {
    const subscription = watch((formData: BlockValues) => {
      memoizedUpdateBlock(formData, selectedBlock);
    });

    return () => {
      subscription.unsubscribe();
    };
  }, [watch, memoizedUpdateBlock, selectedBlock]);
};
